import { useEffect, useRef, useState } from "react";

import styled from "styled-components";

import { Colors } from "../../config/Constants";
import Button from "../Button";
import Radio from "./Radio";
import DatePickerInput from "../Datepicker";
import dayjs from "dayjs";
import { toast } from "react-toastify";

const filterIcon = require("../../assets/images/filter_icon.png");

interface InputProps {
    reportDate: Date;
    value: string;
    onChange: (value: any) => void;
    width: string;
    ghostText?: string;
    filterEntities: any;
    onFilterSelect: any;
    selectedFilter: number;
    onSetReportDate: any;
    onClearFilter: any;
    onAgentSelect: any;
    onSaveFilter: (startDate?: Date, endDate?: Date) => void;
}
interface InputFieldProps {
    value: string;
    onChange: (value: any) => void;
    width: string;
    ghostText?: string;
}

const DateFilter = ({
    reportDate,
    value,
    onChange,
    width,
    ghostText,
    filterEntities,
    onFilterSelect,
    selectedFilter,
    onSetReportDate,
    onClearFilter,
    onSaveFilter,
}: InputProps) => {
    const [showDropdown, setShowDropdown] = useState(false);
    const [fromDate, setFromDate] = useState<Date | null | any>();
    const [toDate, setToDate] = useState<Date | null | any>();
    let today = new Date();

    const wrapperRef = useRef(null);

    const useOutsideAlerter = (ref: any) => {
        useEffect(() => {
            function handleClickOutside(event: any) {
                if (ref.current && !ref.current.contains(event.target)) {
                    if (showDropdown) {
                        toast.warn("Kindly click Save to apply the changes.");
                        setShowDropdown(true);
                    } else {
                        setShowDropdown(false);
                    }
                }
            }
            document.addEventListener("mousedown", handleClickOutside);
            return () => {
                document.removeEventListener("mousedown", handleClickOutside);
            };
        }, [ref, showDropdown]);
    };

    useOutsideAlerter(wrapperRef);

    return (
        <FilterContainer width={width} ref={wrapperRef}>
            <StyledInput
                width="100%"
                type="text"
                value={value}
                onChange={(e) => onChange(e.target.value)}
                onClick={() => setShowDropdown(true)}
                placeholder={ghostText || "Select filter"}
                readOnly
            />
            {showDropdown && (
                <FilterDropDown>
                    <FilterHeader>
                        <div>Visit Date</div>
                        <Button
                            width="4rem"
                            fontColor={Colors.red}
                            onClick={onClearFilter}
                            label="Clear"
                            outlineColor="transparent"
                        />
                    </FilterHeader>
                    <FilterListContainer>
                        {filterEntities.map((entity: any, index: any) => (
                            <Radio
                                key={index}
                                index={index}
                                selected={index === selectedFilter}
                                label={entity.label}
                                onChange={() => {
                                    onFilterSelect(index);
                                    if (index != 4) {
                                        setFromDate(null);
                                        setToDate(null);
                                    }

                                }
                                }
                            />
                        ))}
                        {selectedFilter === 4 &&
                            <DropdownContainer>
                                <DatePickerInput
                                    //@ts-ignore
                                    value={fromDate}
                                    // maxDate={today}
                                    onChange={(e) => {
                                        setFromDate(e);
                                        setToDate(null);
                                    }}
                                    ghostText={"From"}
                                />
                                <DatePickerInput
                                    disabled={!fromDate}
                                    //@ts-ignore
                                    value={toDate}
                                    minDate={dayjs(fromDate).toDate()}
                                    // maxDate={today}
                                    onChange={setToDate}
                                    ghostText={"To"}
                                />
                            </DropdownContainer>
                        }
                    </FilterListContainer>
                    <FilterAction>
                        <Button
                            onClick={() => {
                                setShowDropdown(false);
                                onSetReportDate(fromDate, toDate);
                                if (selectedFilter != 4) {
                                    setFromDate(null);
                                    setToDate(null);
                                }
                            }}
                            label="Save"
                            color={Colors.red}
                            outlineColor={Colors.red}
                            fontColor={Colors.white}
                        />
                        <Button
                            onClick={() => setShowDropdown(false)}
                            label="Cancel"
                            outlineColor={Colors.red}
                            fontColor={Colors.red}
                        />
                    </FilterAction>
                </FilterDropDown>
            )}
        </FilterContainer>
    );
};

interface InputProps {
    width: string;
    ghostText?: string;
}
interface FilterProps {
    width: string;
}

const FilterListContainer = styled.div`
  margin: 1rem 0;
  margin-top: 0.25rem;
`;
const FilterHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`;
const FilterAction = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
`;

const FilterContainer = styled.div<FilterProps>`
  position: relative;
  width: ${(props) => props.width};
  @media only screen and (max-width: 1024px) {
    width: 100%
  }
`;
const FilterDropDown = styled.div`
  position: absolute;
  padding: 0.5rem 1rem;
  background: white;
  border: 1px solid ${Colors.athensGray};
  width: 100%;
  border-radius: 0.25rem;
  margin-top: 0.25rem;
  @media only screen and (max-width: 1024px) {
    z-index: 10;
    width: 90%;
  }
`;

const StyledInput = styled.input<InputFieldProps>`
  opacity: 1;
  display: block;
  background: ${`url(${filterIcon}) no-repeat right`};
  background-position: calc(100% - 0.625rem);
  margin-top: 0.5rem;
  cursor: ${(props) => (props.disabled ? "not-allowed" : "pointer")};
  border-radius: 0.25rem;
  width: ${(props) => (props.width ? props.width : "100%")};
  height: 2.6875rem;
  font-family: "Poppins-Medium";
  font-size: 0.8125rem;
  border: 0.0625rem solid rgb(228, 230, 238);
  padding: 0 0.5rem;
  box-sizing: border-box;
  color: ${Colors.secondaryBlack};
  :focus {
    outline: none;
  }
  ::placeholder {
    font-family: "Poppins-Book";
    font-size: 0.8125rem;
    color: ${Colors.cloudyGrey};
  }
`;
const DropdownContainer = styled.div`
  margin-inline: 1.5rem; 
  margin-bottom: 0.5rem;
  @media only screen and (max-width: 1024px) {
    display: flex;
    flex-direction: column;
  }
`;

const Divider = styled.div`
  border-bottom: 1px solid ${Colors.athensGray};
  margin: 1rem 0;
`;

const FilterSubHeader = styled.div`
  font-weight: bold;
  margin-top: 1rem;
  margin-bottom: 0.5rem;
`;

const Label = styled.label`
  font-family: "Poppins-Medium";
  font-size: 0.8rem;
  color: ${Colors.comet};
  margin-top: 1.56rem;
  margin-bottom: 0.625rem;
`;




export default DateFilter;
